import {
	type FullStackPack,
	type StackPack,
	type StackPackConfiguration,
	StackPackInstanceState,
	type StackPackIntegration,
	StackPackStatus,
	isFullStackPack,
} from "~/models/dto";
import { isPresent } from "~/tools/is-present";
import type { Record } from "~/tools/record";
import { type RequestStatus, isLoadingRequestStatus } from "~/tools/request-status";

import {
	type StackPacksFilter,
	StackPacksFilterStatus,
} from "../../app/state/pages/stackPacks/index/StackPacksIndexTypes";

const isStackPack = (stackPack: FullStackPack, f: (conf: StackPackConfiguration) => boolean): boolean =>
	stackPack.configurations.some(f);

export const isStackPackBroken = (stackPack: FullStackPack): boolean =>
	isStackPack(stackPack, (conf) => conf.status === StackPackInstanceState.Error);

export const isStackPackEnabled = (stackPack: FullStackPack): boolean => stackPack.configurations.length !== 0;

export const isStackPackUpgrading = (stackPack: FullStackPack): boolean =>
	isStackPack(stackPack, (conf) => conf.status === StackPackInstanceState.Upgrading);

export const isStackPackUpgraded = (stackPack: FullStackPack): boolean => {
	const { configurations, nextVersion } = stackPack;
	if (!nextVersion || configurations.length === 0) {
		return true;
	}

	return configurations.every(({ stackPackVersion }) => stackPackVersion === nextVersion.version);
};

export const getFirstFailedConfiguration = (stackPack: FullStackPack): StackPackConfiguration | undefined =>
	stackPack.configurations.find(({ status }) => status === StackPackInstanceState.Error);

export const isStackPackConfigurationInstalled = (configuration: StackPackConfiguration | undefined): boolean =>
	isPresent(configuration?.id);

export const isStackPackPending = (stackPack: FullStackPack): boolean =>
	stackPack.releaseStatus === StackPackStatus.ComingSoon;

export const isFilterActive = (filter: StackPacksFilter | undefined): filter is StackPacksFilter => {
	if (!filter) {
		return false;
	}

	if (filter.status) {
		return true;
	}
	if (filter.categories && filter.categories.length !== 0) {
		return true;
	}

	return false;
};

export type FilterPredicate<T> = (t: T | undefined) => boolean;

const NOOP_PREDICATE: FilterPredicate<any> = (value: any) => Boolean(value);

const FILTER_PREDICATES_BY_STATUS = {
	[StackPacksFilterStatus.AVAILABLE]: () => true,
	[StackPacksFilterStatus.BROKEN]: isStackPackBroken,
	[StackPacksFilterStatus.ENABLED]: isStackPackEnabled,
	[StackPacksFilterStatus.PENDING]: isStackPackPending,
} satisfies Record<StackPacksFilterStatus>;

const FILTER_LABELS_BY_STATUS = {
	[StackPacksFilterStatus.AVAILABLE]: "Available",
	[StackPacksFilterStatus.BROKEN]: "Broken",
	[StackPacksFilterStatus.ENABLED]: "Installed",
	[StackPacksFilterStatus.PENDING]: "Coming Soon",
} satisfies Record<StackPacksFilterStatus>;

// A helper function to compare a (possibly empty) list of categories
// to a stack pack or a stack pack integration
const doCategoriesMatch = (
	categories: Array<string> | undefined,
	stackPackOrIntegration: StackPack | StackPackIntegration | undefined,
): boolean => {
	if (!categories || categories.length === 0) {
		return true;
	}
	if (!stackPackOrIntegration) {
		return false;
	}

	const { categories: stackPackOrIntegrationCategories } = stackPackOrIntegration;
	if (stackPackOrIntegrationCategories.length === 0) {
		return false;
	}

	return categories.every((category: string) => stackPackOrIntegrationCategories.includes(category));
};

// A helper function to compare a text to a stack pack or a stack pack integration
const doesTextMatch = (text: string | undefined, stackPackOrIntegration: StackPack | StackPackIntegration): boolean => {
	const normalizedText: string = (text || "").trim().toLowerCase();
	const normalizedName: string = (stackPackOrIntegration.name || "").toLowerCase();
	const normalizedDisplayName: string = (stackPackOrIntegration.displayName || "").toLowerCase();
	const version = "version" in stackPackOrIntegration ? stackPackOrIntegration.version || "" : "";

	return (
		normalizedName.includes(normalizedText) ||
		normalizedDisplayName.includes(normalizedText) ||
		version.includes(normalizedText)
	);
};

export const getStackPackIntegrationTextFilter =
	(text: string | undefined): FilterPredicate<StackPackIntegration> =>
	(integration) =>
		integration != null && doesTextMatch(text, integration);

export const getStackPackIntegrationFilter = (
	filter: StackPacksFilter | undefined,
): FilterPredicate<StackPackIntegration> => {
	if (!filter) {
		return NOOP_PREDICATE;
	}

	return (integration) => integration != null && doCategoriesMatch(filter.categories, integration);
};

export const getStackPackStatusFilter = (
	status: StackPacksFilterStatus | undefined,
): FilterPredicate<FullStackPack> => {
	if (!status) {
		return NOOP_PREDICATE;
	}

	const predicate = FILTER_PREDICATES_BY_STATUS[status];

	return (stackPack) => stackPack != null && predicate(stackPack);
};

const integrationsMatchPredicate = (stackPack: StackPack, predicate: FilterPredicate<StackPackIntegration>): boolean =>
	isFullStackPack(stackPack) && stackPack.integrations.some(predicate);

export const getStackPackCategoryFilter = (categories: Array<string> | undefined): FilterPredicate<StackPack> => {
	if (!categories || categories.length === 0) {
		return NOOP_PREDICATE;
	}

	return (stackPack) => {
		if (doCategoriesMatch(categories, stackPack)) {
			return true;
		}
		if (!isFullStackPack(stackPack)) {
			return false;
		}

		return stackPack.integrations.some((stackPackIntegration) => doCategoriesMatch(categories, stackPackIntegration));
	};
};

export const getStackPackTextFilter = (text: string | undefined): FilterPredicate<StackPack> => {
	if (!text?.trim()) {
		return NOOP_PREDICATE;
	}

	const integrationsPredicate = getStackPackIntegrationTextFilter(text);

	return (stackPack) => {
		if (stackPack == null) {
			return false;
		}

		if (integrationsMatchPredicate(stackPack, integrationsPredicate)) {
			return true;
		}
		return doesTextMatch(text, stackPack);
	};
};

export const getStackPackFilter = (filter: StackPacksFilter | undefined): FilterPredicate<FullStackPack> => {
	if (!isFilterActive(filter)) {
		return NOOP_PREDICATE;
	}

	const categoryPredicate = getStackPackCategoryFilter(filter.categories);
	const statusPredicate = getStackPackStatusFilter(filter.status);

	return (stackPack) => stackPack != null && statusPredicate(stackPack) && categoryPredicate(stackPack);
};

export const getFilterStatusLabel = (status: StackPacksFilterStatus): string => FILTER_LABELS_BY_STATUS[status];

export const getStatusForInstance = (configuration?: StackPackConfiguration): StackPackInstanceState =>
	configuration?.status ?? StackPackInstanceState.NotInstalled;

export const getStackPackInstanceStatusInText = (
	config?: StackPackConfiguration,
	provisionStatus?: RequestStatus<Error, true>,
	deprovisionStatus?: RequestStatus<Error, true>,
): string => {
	if (isLoadingRequestStatus(provisionStatus)) {
		return "Installing...";
	}

	if (isLoadingRequestStatus(deprovisionStatus)) {
		return "Uninstalling...";
	}

	const status = getStatusForInstance(config);
	switch (status) {
		case StackPackInstanceState.Provisioning:
			return "Installing...";
		case StackPackInstanceState.WaitingForData:
			return "Installed. Waiting for data...";
		case StackPackInstanceState.WaitingForManualSteps:
			return "Waiting for user action...";
		case StackPackInstanceState.Installed:
			return "Active";
		case StackPackInstanceState.Error:
			return "Error";
		case StackPackInstanceState.NotInstalled:
			return "Not installed yet";
		case StackPackInstanceState.Deprovisioning:
			return "Uninstalling...";
		case StackPackInstanceState.Upgrading:
			return "Upgrading...";
		default:
			return "Not installed yet";
	}
};

export const orderAllStackPacksConfigurations = (stackPacks: Array<FullStackPack>): Array<FullStackPack> =>
	stackPacks.map<FullStackPack>((stackPack) => ({
		...stackPack,
		configurations: stackPack.configurations.sort((a, b) => (a.id! <= b.id! ? -1 : 1)),
	}));
